/**
 * @file usePriorMonthConfirm.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-11-02
 * @description "Dated in a month that should be locked" — the question every
 *              invoice and bill save asks when the server answers 409
 *              PRIOR_MONTH_CONFIRM (Cam, 15 Oct 2026; the rule is
 *              utils/xeroLockMonth, the server's check
 *              xeroLockService.assertPriorMonthConfirmed).
 *
 * One dialog for every screen: the server's own words (title + message),
 * "Save anyway" / "Go back". Save anyway → the screen sends the same request
 * again with `confirm_prior_month: true`; Go back → nothing is saved and the
 * screen stays as it was (no error shown).
 *
 *   const priorMonth = usePriorMonthConfirm();
 *   …
 *   if (isPriorMonthConfirm(result)) {
 *     if (await priorMonth.ask(result)) return save({ ...opts, priorMonthConfirmed: true });
 *     return;   // went back
 *   }
 *   …
 *   {priorMonth.dialog}
 *
 * `run(send)` does the whole round for a plain fetch: send(extraBody) →
 * Response; answers { res, data } (use `data` — the body has been read) or
 * { declined: true }.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import { PRIOR_MONTH_CODE } from '../utils/xeroLockMonth';
import './usePriorMonthConfirm.css';

// DD/MM/YYYY from 'YYYY-MM-DD'.
const ukDay = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(d || '');
};

/** Is this answer the prior-month question? */
export function isPriorMonthConfirm(body) {
  return !!body && body.code === PRIOR_MONTH_CODE;
}

/** The question's words from an answer (bills put them at the top level, invoices under details). */
export function priorMonthWords(body) {
  const pm = (body && (body.prior_month || body.details?.prior_month)) || {};
  return {
    title: pm.title || 'Dated in a month that should be locked',
    message: pm.message || body?.error || 'This is dated in a month that should be locked in Xero by now. Only go on if the lock has been taken off on purpose for this.',
    dates: Array.isArray(pm.dates) ? pm.dates : [],
    // Several documents: the question gives a count; these are listed in a
    // window of their own (Cam, 2 Nov 2026 — Till Sales named dozens).
    items: Array.isArray(pm.items) ? pm.items : []
  };
}

/** A response's JSON, read once — the caller uses `data`, not res.json(). */
async function readJson(res) {
  try {
    return await (typeof res.clone === 'function' ? res.clone() : res).json();
  } catch (_) {
    return null;
  }
}

/** The body extra a confirmed resend carries. */
export const PRIOR_MONTH_CONFIRMED = Object.freeze({ confirm_prior_month: true });

export default function usePriorMonthConfirm() {
  const [question, setQuestion] = useState(null);
  const [showList, setShowList] = useState(false);
  const resolver = useRef(null);

  // Answer a question left open if the screen goes away: "go back".
  useEffect(() => () => {
    if (resolver.current) resolver.current(false);
    resolver.current = null;
  }, []);

  /** Show the question for a PRIOR_MONTH_CONFIRM answer (or { title, message }); resolves true / false. */
  const ask = useCallback((body) => new Promise((resolve) => {
    if (resolver.current) resolver.current(false);
    resolver.current = resolve;
    setShowList(false);
    setQuestion(body && body.code ? priorMonthWords(body)
      : { title: body?.title, message: body?.message, items: Array.isArray(body?.items) ? body.items : [] });
  }), []);

  const answer = useCallback((yes) => {
    const r = resolver.current;
    resolver.current = null;
    setQuestion(null);
    setShowList(false);
    if (r) r(yes);
  }, []);

  /**
   * Send, and when the server asks, ask the person and send again confirmed.
   * @param {(extra: object) => Promise<Response>} send
   * @returns {Promise<{ res: Response, data: any } | { declined: true }>}
   */
  const run = useCallback(async (send) => {
    const res = await send({});
    const data = await readJson(res);
    if (res.status === 409 && isPriorMonthConfirm(data)) {
      if (!(await ask(data))) return { declined: true };
      const again = await send(PRIOR_MONTH_CONFIRMED);
      return { res: again, data: await readJson(again) };
    }
    return { res, data };
  }, [ask]);

  const items = question?.items || [];
  // The list opens in its own window, the question set aside while it is
  // open (the question sits above every Modal) and back when it closes.
  const dialog = (
    <>
      <ConfirmDialog
        isOpen={!!question && !showList}
        type="warning"
        title={question?.title || 'Dated in a month that should be locked'}
        message={items.length > 1 ? (
          <div className="prior-month-question">
            <p>{question?.message || ''}</p>
            <button type="button" className="prior-month-count" onClick={() => setShowList(true)}>
              <span className="material-symbols-rounded">list</span>
              See the {items.length}
            </button>
          </div>
        ) : (question?.message || '')}
        confirmText="Save anyway"
        confirmIcon="save"
        cancelText="Go back"
        onConfirm={() => answer(true)}
        onCancel={() => answer(false)}
      />
      <Modal
        isOpen={!!question && showList}
        onClose={() => setShowList(false)}
        title={`${items.length} dated in a month that should be locked`}
        className="prior-month-list"
        footer={(
          <div className="buttons slideout">
            <div>
              <button type="button" className="btn-slideout" onClick={() => setShowList(false)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
                <span className="btn-label">Back to the question</span>
              </button>
            </div>
          </div>
        )}
      >
        <ol className="prior-month-items">
          {items.map((it, i) => (
            <li key={`${it.what}-${i}`}>
              <span className="prior-month-what">{it.what}</span>
              <span className="prior-month-dates">{(it.dates || []).map(ukDay).join(', ')}</span>
            </li>
          ))}
        </ol>
      </Modal>
    </>
  );

  return { ask, run, dialog, open: !!question };
}
